import React, { useState, useEffect, useRef, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import LanguageSwitcher from './LanguageSwitcher';
import { LanguageContext } from '../hooks/useLanguage';
import { nav } from '../data/content';
import { SHOW } from '../config/visibility';

const LogoIcon = () => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const pieces = svg.querySelectorAll('.bolt-piece');
    gsap.set(pieces, { opacity: 0, y: -6 });
    gsap.to(pieces, {
      opacity: 1,
      y: 0,
      duration: 0.5,
      stagger: 0.15,
      ease: 'power2.out',
      delay: 0.25,
    });
  }, []);

  return (
    <svg ref={svgRef} viewBox="0 0 20 58" width="16" height="52" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
      <defs>
        <linearGradient id="boltGradH" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#D4AE5C" />
          <stop offset="100%" stopColor="#A8893A" />
        </linearGradient>
      </defs>
      <polygon className="bolt-piece" points="18,2 6,2 3,26 15,26" fill="url(#boltGradH)" />
      <polygon className="bolt-piece" points="7,26 19,26 16,50 4,50" fill="url(#boltGradH)" />
    </svg>
  );
};

const Header: React.FC = () => {
  const { lang } = useContext(LanguageContext);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menu = nav[lang];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const allLinks = [
    { label: menu.home, href: '#home', always: true },
    { label: menu.domotica, href: '#services', always: true },
    { label: menu.electricista, href: '#electricidad', always: false, show: SHOW.electricidad },
    { label: menu.projects, href: '#projects', always: true },
    { label: menu.proceso, href: '#proceso', always: true },
    { label: menu.about, href: '#about', always: true },
    { label: menu.faq, href: '#faq', always: true },
    { label: menu.contact, href: '#contact', always: true },
  ];

  const navLinks = allLinks.filter((l) => l.always || l.show);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: scrolled ? 'rgba(13,13,13,0.96)' : 'transparent',
        backdropFilter: scrolled ? 'blur(16px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(200,169,110,0.2)' : 'none',
        transition: 'all 0.4s ease',
      }}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '72px',
        padding: '0 clamp(24px, 4vw, 64px)',
        maxWidth: '1280px',
        margin: '0 auto',
        width: '100%',
      }}>

        <motion.a
          href="#home"
          whileHover={{ opacity: 0.85 }}
          style={{ display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none' }}
        >
          <LogoIcon />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: '13px', lineHeight: 1, textTransform: 'uppercase' }}>
              <span style={{ fontWeight: 700, letterSpacing: '4px', color: '#FFFFFF' }}>VATIO</span>
              {' '}
              <span style={{ fontWeight: 400, letterSpacing: '4px', color: 'rgba(255,255,255,0.42)' }}>CENTRAL</span>
            </span>
            <div style={{ width: '22px', height: '1px', background: '#C8A96E', margin: '6px 0' }} />
            <span style={{
              fontFamily: 'var(--font-mono)',
              fontSize: '7.5px',
              fontWeight: 300,
              letterSpacing: '2px',
              color: '#333',
              textTransform: 'uppercase',
              lineHeight: 1,
            }}>
              Electrical Design · Automation
            </span>
          </div>
        </motion.a>

        <nav style={{ display: 'flex', gap: '28px', alignItems: 'center', marginLeft: 'auto' }} className="desktop-nav">
          {navLinks.map((link) => (
            <motion.a
              key={link.href}
              href={link.href}
              whileHover={{ color: '#C8A96E' }}
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: '12px',
                fontWeight: 400,
                letterSpacing: '2px',
                color: '#AAAAAA',
                textTransform: 'uppercase' as const,
                transition: 'color 0.2s',
              }}
            >
              {link.label}
            </motion.a>
          ))}
          <LanguageSwitcher />
          <motion.a
            href="#contact"
            whileHover={{ background: '#C8A96E', color: '#0D0D0D' }}
            style={{
              padding: '10px 24px',
              border: '1px solid #C8A96E',
              fontFamily: 'var(--font-body)',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '3px',
              textTransform: 'uppercase' as const,
              transition: 'all 0.3s ease',
              color: '#C8A96E',
              display: 'inline-block',
            }}
          >
            {menu.contact}
          </motion.a>
        </nav>

        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="burger"
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            display: 'none', flexDirection: 'column' as const,
            gap: '5px', padding: '4px',
          }}
        >
          {[0, 1, 2].map((i) => (
            <motion.span
              key={i}
              animate={{
                rotate: menuOpen && i === 0 ? 45 : menuOpen && i === 2 ? -45 : 0,
                y: menuOpen && i === 0 ? 8 : menuOpen && i === 2 ? -8 : 0,
                opacity: menuOpen && i === 1 ? 0 : 1,
              }}
              style={{ display: 'block', width: '24px', height: '1.5px', background: '#AAAAAA' }}
            />
          ))}
        </button>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{
              background: 'rgba(13,13,13,0.98)',
              borderTop: '1px solid rgba(200,169,110,0.2)',
              padding: '24px 32px',
              display: 'flex',
              flexDirection: 'column' as const,
              gap: '20px',
            }}
          >
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '16px',
                  fontWeight: 400,
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  color: '#AAAAAA',
                }}
              >
                {link.label}
              </a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @media (max-width: 900px) {
          .burger { display: flex !important; }
          .desktop-nav { display: none !important; }
        }
      `}</style>
    </motion.header>
  );
};

export default Header;
